import { Link } from 'react-router-dom'
import type { History } from '@/lib/api/types'
import { parseHtmlString } from '@/utils/helpers/html'

interface HistoryContentProps {
  historiesByYear: Record<string, History[]>
  isLoading: boolean
}

const HistoryContent = ({ historiesByYear, isLoading }: HistoryContentProps) => {
  // Get all years sorted in descending order
  const allHistoryItems = Object.keys(historiesByYear).sort((a, b) =>
    parseInt(b) - parseInt(a)
  )

  if (isLoading) {
    return (
      <div className="space-y-12">
        <div className="text-center py-12">
          <p className="text-lg text-gray-500">Loading history...</p>
        </div>
      </div>
    )
  }

  if (allHistoryItems.length === 0) {
    return (
      <div className="space-y-12">
        <div className="text-center py-12">
          <p className="text-lg text-gray-500">No history items found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-12">
      {allHistoryItems.map((year) => {
        const histories = historiesByYear[year] || []

        return histories.map((history, index) => (
          <Link
            key={`${history.historyId}-${index}`}
            to={`/about/history/${history.historyId}`}
            id={index === 0 ? `section-${year}` : undefined}
            className="block scroll-mt-32 lg:scroll-mt-24 group"
          >
            <article>
              <div className="bg-white overflow-hidden">
                <img
                  className="w-full h-64 md:h-80 lg:h-96 object-cover group-hover:opacity-90 transition-opacity"
                  src={history.imageUrl}
                  alt={history.title}
                />

                <div className="py-6 md:py-8 w-full">
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-wine-100 text-white">
                        {year}
                      </span>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-normal font-pt-serif text-gray-100 leading-tight group-hover:text-wine-100 transition-colors">
                      {history.title}
                    </h3>

                    <div className="prose prose-lg max-w-none">
                      <p className="text-lg font-normal font-satoshi text-gray-700 leading-relaxed whitespace-pre-line line-clamp-3">
                        <span dangerouslySetInnerHTML={parseHtmlString(history.description)} />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        ))
      })}
    </div>
  )
}

export default HistoryContent
